import { Type } from '@angular/core';
import { FormulaType, SrcVariableType } from '@iot-platform/models/common';

export interface Formula {
  model: FormulaType | null;
  parameters: FormulaParameters | null;
  srcVariables: FormulaSourceVariables;
  isActive?: boolean; // TODO: MAKE IT REQUIRED WHEN BREAK FORMULA TICKET IS DONE
  version?: number | null;
}

export const ASSET_VARIABLE_ALL_FORMULA_TYPES = [
  FormulaType.EQUAL,
  FormulaType.MIN_MAX,
  FormulaType.MIN_MAX_ADVANCED,
  FormulaType.SLOPE_SHIFT,
  FormulaType.LINEARIZATION,
  FormulaType.CONSTANT,
  FormulaType.OLD_CONSUMPTION_FOR_PG,
  FormulaType.CONSUMPTION_FOR_PG,
  FormulaType.SHORTFALL,
  FormulaType.AUTONOMY_FOR_PG,
  FormulaType.SUM,
  FormulaType.DAILY_CONSUMPTION
];

export const FORMULA_TYPES_WITH_DEVICE_VARIABLE_SOURCE = [
  FormulaType.EQUAL,
  FormulaType.MIN_MAX,
  FormulaType.MIN_MAX_ADVANCED,
  FormulaType.SLOPE_SHIFT,
  FormulaType.LINEARIZATION
];

export enum Samplings {
  OFF = 'off',
  FIFTEEN_MINUTES = '15min',
  ONE_HOUR = '1h'
}

export const TOLERANCE_VALUES: { value: number; min: number; label: string }[] = [
  { value: 0, min: 0, label: '' },
  { value: 1, min: 1, label: 'min' },
  { value: 1, min: 60, label: 'hr' },
  { value: 2, min: 120, label: 'hrs' }
];

export type FormulaConfig = {
  model: FormulaType;
  minSrcVariables: number;
  maxSrcVariables: number;
  unitMatchingRequired: boolean;
  srcVariableType?: SrcVariableType;
  srcVariableSlots?: FormulaSourceVariableSlot[];
  paramsFormComponent?: Type<unknown>;
  getParamsFormInputs?: (...args: unknown[]) => unknown;
  getInitialParamsFormInputs?: (...args: unknown[]) => { [key: string]: unknown };
  getParams: (...args: unknown[]) => object;
  getSrcVariableParams: (...args: unknown[]) => object;
  getParamsErrors: (...args: unknown[]) => string | null;
  isSrcVariablesParamsChanged: (...args: unknown[]) => boolean;
};

export interface FormulaSourceVariableSlot {
  name: string;
  labelKey?: string;
  placeholderKey?: string;
  requiredUnit?: string;
  requiredUnitCategory?: string;
  requiredFormulaModels?: FormulaType[];
}

export const FORMULA_TYPES_WITH_ASSET_VARIABLE_SOURCE = [
  FormulaType.OLD_CONSUMPTION_FOR_PG,
  FormulaType.CONSUMPTION_FOR_PG,
  FormulaType.SHORTFALL,
  FormulaType.AUTONOMY_FOR_PG,
  FormulaType.SUM,
  FormulaType.DAILY_CONSUMPTION
];

// Parameters

export type FormulaParameters =
  | FormulaDefaultParameters
  | ConstantParameters
  | MinMaxParameters
  | MinMaxAdvancedParameters
  | HorizontalTankLevelParameters
  | SphericalTankLevelParameters
  | SlopeShiftParameters
  | LinearizationParameters
  | SumParameters;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface FormulaDefaultParameters {
  sampling?: Samplings;
}

export interface ConstantParameters {
  constantValue: number;
}

export interface HorizontalTankLevelParameters {
  maxRead: number;
}

export interface SphericalTankLevelParameters {
  maxRead: number;
}

export interface MinMaxParameters {
  maxRead: number;
  maxDisplay: number;
}

export interface MinMaxAdvancedParameters {
  minRead: number;
  maxRead: number;
  minDisplay: number;
  maxDisplay: number;
}

export interface LinearizationParameters {
  headHeight: number;
  diameter: number;
  length: number;
  density: number;
  maxDisplay: number;
  maxRead: number;
}

export interface SlopeShiftParameters {
  slope: number;
  shift: number;
}

export interface SumParameters {
  toleranceInMinutes: number;
}

// SrcVariables

type FormulaSourceVariableType = 'device-variable' | 'asset-variable';

export interface FormulaSourceVariable {
  name: string;
  originId: string;
  type: FormulaSourceVariableType;
  variableId: string;
}

export type FormulaSourceVariables =
  | DefaultFormulaSourceVariable
  | AutonomyForPGSrcVariables
  | OldConsumptionForPGSrcVariables
  | ConsumptionForPGSrcVariables
  | ShortfallSrcVariables
  | AverageAdvancedVariable;

export interface DefaultFormulaSourceVariable {
  [key: string]: FormulaSourceVariable;
}

export interface AverageAdvancedVariable {
  name: string;
  originId: string;
  type: FormulaSourceVariableType;
  ratio: number;
  variableId: string;
}

// Consumption
export interface OldConsumptionForPGSrcVariables {
  'left-pressure': OldConsumptionForPGSrcVariableLeftPressure;
  'right-pressure': OldConsumptionForPGSrcVariableRightPressure;
  'left-volume': OldConsumptionForPGSrcVariableLeftVolume;
  'right-volume': OldConsumptionForPGSrcVariableRightVolume;
}

interface OldConsumptionForPGSrcVariableLeftPressure extends FormulaSourceVariable {
  name: 'left-pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface OldConsumptionForPGSrcVariableLeftVolume extends FormulaSourceVariable {
  name: 'left-volume';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface OldConsumptionForPGSrcVariableRightPressure extends FormulaSourceVariable {
  name: 'right-pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface OldConsumptionForPGSrcVariableRightVolume extends FormulaSourceVariable {
  name: 'right-volume';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

// Consumption (new formula, adds a max-pressure slot on top of the legacy 4 slots)
export interface ConsumptionForPGSrcVariables {
  'left-pressure': ConsumptionForPGSrcVariableLeftPressure;
  'right-pressure': ConsumptionForPGSrcVariableRightPressure;
  'left-volume': ConsumptionForPGSrcVariableLeftVolume;
  'right-volume': ConsumptionForPGSrcVariableRightVolume;
  'max-pressure': ConsumptionForPGSrcVariableMaxPressure;
}

interface ConsumptionForPGSrcVariableLeftPressure extends FormulaSourceVariable {
  name: 'left-pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface ConsumptionForPGSrcVariableLeftVolume extends FormulaSourceVariable {
  name: 'left-volume';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface ConsumptionForPGSrcVariableRightPressure extends FormulaSourceVariable {
  name: 'right-pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface ConsumptionForPGSrcVariableMaxPressure extends FormulaSourceVariable {
  name: 'max-pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface ConsumptionForPGSrcVariableRightVolume extends FormulaSourceVariable {
  name: 'right-volume';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

// Autonomy
export interface AutonomyForPGSrcVariables {
  pressure: AutonomyForPGSrcVariablePressure;
  volume: AutonomyForPGSrcVariableVolume;
  consumption: AutonomyForPGSrcVariableConsumption;
  'minimum-usable-pressure': AutonomyForPGSrcVariableMinimumUsablePressure;
}

interface AutonomyForPGSrcVariablePressure extends FormulaSourceVariable {
  name: 'pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface AutonomyForPGSrcVariableVolume extends FormulaSourceVariable {
  name: 'volume';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface AutonomyForPGSrcVariableConsumption extends FormulaSourceVariable {
  name: 'consumption';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface AutonomyForPGSrcVariableMinimumUsablePressure extends FormulaSourceVariable {
  name: 'minimum-usable-pressure';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

// Shortfall
export interface ShortfallSrcVariables {
  'contractual-flow': ShortfallSrcVariableContractualFlow;
  'production-flow': ShortfallSrcVariableProductionFlow;
  'total-flow': ShortfallSrcVariableTotalFlow;
}

interface ShortfallSrcVariableContractualFlow extends FormulaSourceVariable {
  name: 'contractual-flow';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface ShortfallSrcVariableProductionFlow extends FormulaSourceVariable {
  name: 'production-flow';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

interface ShortfallSrcVariableTotalFlow extends FormulaSourceVariable {
  name: 'total-flow';
  variableId: string;
  type: 'asset-variable';
  originId: string;
}

export interface SourceVariable {
  id: string;
  name: string;
  type: 'asset' | 'device';
}
